import * as React from "react";
import { cn } from "@/lib/utils";

// Chromium's native <input type="date"> lets the year segment keep accepting
// digits past 4 (typing quickly can produce "202601-06-15" etc.) -- min/max
// alone only affects :invalid styling, not what .value returns, so every
// date field in the app is guarded here at the source: out-of-range years
// are silently dropped instead of reaching the caller's onChange/state.
const DATE_MIN_YEAR = 1900;
const DATE_MAX_YEAR = 2100;
const DEFAULT_DATE_MIN = `${DATE_MIN_YEAR}-01-01`;
const DEFAULT_DATE_MAX = `${DATE_MAX_YEAR}-12-31`;

function isSaneDateValue(value: string): boolean {
  if (!value) return true;
  const year = Number(value.slice(0, value.indexOf("-")));
  return Number.isFinite(year) && year >= DATE_MIN_YEAR && year <= DATE_MAX_YEAR;
}

function Input({ className, type, onChange, min, max, ...props }: React.ComponentProps<"input">) {
  const isDate = type === "date";

  return (
    <input
      type={type}
      data-slot="input"
      min={isDate ? (min ?? DEFAULT_DATE_MIN) : min}
      max={isDate ? (max ?? DEFAULT_DATE_MAX) : max}
      onChange={
        isDate
          ? (e) => { if (isSaneDateValue(e.target.value)) onChange?.(e); }
          : onChange
      }
      className={cn(
        "flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-base shadow-xs transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        // A native type="date" control's calendar-icon affordance competes
        // for space with the rendered dd/mm/yyyy text inside the same box;
        // in a half-width grid cell on a narrow phone, Chrome drops the
        // icon entirely once it doesn't fit, so the field looks pickerless.
        // Smaller text + tighter padding buys back the room it needs.
        isDate && "text-sm px-2",
        className
      )}
      {...props}
    />
  );
}

export { Input };
